// The single place that knows about transport concerns: cookies, CSRF proof,
// and the one retry a session refresh is allowed to buy.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: unknown,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export interface SessionState {
  csrfToken: string;
  expiresAt: string;
}

export type SessionCheckResult = "authenticated" | "unauthenticated" | "unavailable";

let csrfToken: string | null = null;
let session: SessionState | null = null;
let refreshInFlight: Promise<SessionCheckResult> | null = null;
let sessionGeneration = 0;
const sessionListeners = new Set<(authenticated: boolean) => void>();

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

function notifySessionChanged(): void {
  const authenticated = hasCurrentSession();
  sessionListeners.forEach((listener) => listener(authenticated));
}

export function subscribeToSession(listener: (authenticated: boolean) => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

export function clearLocalSession(): void {
  sessionGeneration += 1;
  session = null;
  setCsrfToken(null);
  sessionListeners.forEach((listener) => listener(false));
}

export function hasCurrentSession(): boolean {
  return session !== null && Date.parse(session.expiresAt) > Date.now();
}

export function expireLocalSessionIfNeeded(): boolean {
  if (session && Date.parse(session.expiresAt) <= Date.now()) {
    clearLocalSession();
    return true;
  }
  return false;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  // CSRF is required on every mutating call; GETs never carry it.
  csrf?: boolean;
  // Logout has a token in both header and body and performs its own bounded retry.
  csrfRetry?: boolean;
  headers?: Record<string, string>;
}

async function raw(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...options.headers };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.csrf) {
    if (!csrfToken) throw new ApiError(403, "csrf_token_missing");
    headers["X-CSRF-Token"] = csrfToken;
  }
  return fetch(path, {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: "same-origin",
  });
}

function errorCode(status: number, payload: unknown): string {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const error = record["error"];
    if (typeof error === "string") return error;
    const message = record["message"];
    if (typeof message === "string") return message;
  }
  return `http_${status}`;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const requestGeneration = sessionGeneration;
  let response = await raw(path, options);

  // A stale CSRF proof (session rotated server-side) is recoverable exactly once.
  if (response.status === 403 && options.csrf && options.csrfRetry !== false) {
    const refreshed = await refreshSession();
    if (refreshed === "authenticated") response = await raw(path, options);
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    if (response.status === 401 && requestGeneration === sessionGeneration) clearLocalSession();
    throw new ApiError(response.status, errorCode(response.status, payload), payload);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function currentSession(): SessionState | null {
  return hasCurrentSession() ? session : null;
}

function validatedSession(value: unknown): SessionState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.csrfToken !== "string" || candidate.csrfToken.length === 0) return null;
  if (typeof candidate.expiresAt !== "string") return null;
  const expiry = Date.parse(candidate.expiresAt);
  if (!Number.isFinite(expiry) || expiry <= Date.now()) return null;
  return { csrfToken: candidate.csrfToken, expiresAt: candidate.expiresAt };
}

export async function login(password: string): Promise<SessionState> {
  const payload = await request<SessionState>("/admin/v1/session/login", {
    method: "POST",
    body: { password },
  });
  const state = validatedSession(payload);
  if (!state) throw new ApiError(502, "invalid_session_response", payload);
  sessionGeneration += 1;
  session = state;
  setCsrfToken(state.csrfToken);
  notifySessionChanged();
  return state;
}

async function performSessionRefresh(): Promise<SessionCheckResult> {
  const refreshGeneration = sessionGeneration;
  try {
    const payload = await request<SessionState & { principal: string }>("/admin/v1/session/me");
    if (refreshGeneration !== sessionGeneration) return hasCurrentSession() ? "authenticated" : "unavailable";
    const state = validatedSession(payload);
    if (!state) return "unavailable";
    session = state;
    setCsrfToken(state.csrfToken);
    notifySessionChanged();
    return "authenticated";
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return "unauthenticated";
    return "unavailable";
  }
}

export function refreshSession(): Promise<SessionCheckResult> {
  if (!refreshInFlight) {
    refreshInFlight = performSessionRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function postLogout(): Promise<void> {
  const token = csrfToken;
  if (!token) throw new ApiError(403, "csrf_token_missing");
  await request<void>("/admin/v1/session/logout", {
    method: "POST",
    body: { csrfToken: token },
    csrf: true,
    csrfRetry: false,
  });
}

export async function logout(): Promise<void> {
  if ((await refreshSession()) !== "authenticated") throw new ApiError(401, "session_invalid");
  try {
    await postLogout();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 403) throw error;
    if ((await refreshSession()) !== "authenticated") throw error;
    await postLogout();
  }
  clearLocalSession();
}
